"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Truck, ClipboardCheck, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Clock, ChevronRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  stepNumber: "01",
  title: "Call or Request Service",
  description: "Contact our 24/7 Jonesboro dispatch line. Describe your emergency and get an immediate arrival timeframe.",
  icon: PhoneCall,
  highlight: "Average call answer: Under 15 seconds"
}, {
  stepNumber: "02",
  title: "Technician Dispatched",
  description: "A licensed local Georgia plumber is mobilized immediately with a fully-equipped service vehicle.",
  icon: Truck,
  highlight: "Live status updates provided"
}, {
  stepNumber: "03",
  title: "Diagnosis & Upfront Quote",
  description: "We inspect the problem on-site and present a clear, flat-rate quote before any repair work begins.",
  icon: ClipboardCheck,
  highlight: "No surprise fees or hidden add-ons"
}, {
  stepNumber: "04",
  title: "Job Completed & Verified",
  description: "Your plumbing system is restored, leak-tested under pressure, and cleaned before our technician leaves.",
  icon: CheckCircle2,
  highlight: "100% Satisfaction & parts guarantee"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="how-service-works" data-nav="light" className="relative bg-background text-foreground py-14 md:py-20 lg:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-semibold tracking-wide uppercase mb-4 border border-border/60">
            <Clock className="size-3.5 text-primary" />
            <span>Fast Problem-Solving Process</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-default leading-tight">
            How Our Emergency Service Works
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
            When water is flooding your floor or drains are backed up, you need a straightforward, dependable plan. Here is our 4-step dispatch procedure.
          </p>
        </div>

        {/* Steps Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <motion.div key={step.stepNumber} variants={itemVariants} className="flex">
                <Card data-index={i} className="relative flex-1 flex flex-col justify-between bg-card text-card-foreground border border-border hover:border-primary/50 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 rounded-xl overflow-hidden p-6">
                  {/* Top Header Row in Card */}
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="size-12 rounded-lg bg-secondary text-primary flex items-center justify-center border border-border/60 shadow-2xs shrink-0">
                      <IconComponent className="size-6" />
                    </div>
                    <span className="text-3xl font-extrabold text-muted-foreground/40 font-default tracking-tighter">
                      {step.stepNumber}
                    </span>
                  </div>

                  {/* Body Content */}
                  <CardContent className="p-0 flex-1 flex flex-col">
                    <h3 className="text-lg md:text-xl font-bold text-foreground font-default mb-2.5">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6 flex-1">
                      {step.description}
                    </p>

                    {/* Highlight Footer */}
                    <div className="pt-4 border-t border-border/80 flex items-center gap-2 text-xs font-medium text-foreground">
                      <div className="size-1.5 rounded-full bg-accent shrink-0" />
                      <span className="truncate">{step.highlight}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Trust & Dispatch Commitment Banner */}
        <div className="mt-12 md:mt-16 bg-card border border-border rounded-xl p-6 md:p-8 shadow-sm">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-8">
            <div className="flex items-start sm:items-center gap-4 text-left">
              <div className="size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-xs">
                <ShieldCheck className="size-6 text-accent" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-bold text-foreground font-default">
                  Need Immediate Help in Jonesboro, GA?
                </h4>
                <p className="text-sm text-muted-foreground font-sans mt-0.5">
                  Our emergency vans are stocked with common parts to resolve most urgent repairs on the spot.
                </p>
              </div>
            </div>

            {/* Signature Dual CTA Cluster */}
            <div className="flex flex-wrap items-center justify-center lg:justify-end gap-3.5 w-full lg:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-md rounded-full px-6 transition-[background-color,box-shadow]">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 shrink-0" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-secondary font-semibold border-border rounded-full px-6 transition-[background-color,border-color]">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Service Online</span>
                  <ChevronRight className="size-4 text-muted-foreground" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}